import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, Users, Film, Calendar, Loader2, Plus, X, Sparkles, GitMerge, Check } from "lucide-react";
import { toast } from "sonner";
import AutocompleteInput from "@/components/AutocompleteInput";
import DeleteButton from "@/components/DeleteButton";

/**
 * `/associations` — top-level index. Every distinct association across
 * the video library, with event/clip/admin counts. Click through to
 * `/associations/:name` for the detail page.
 */
export default function AssociationsIndex() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSmart, setShowSmart] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventQ, setEventQ] = useState("");

  const load = () => {
    setLoading(true);
    return api.get("/associations")
      .then((r) => setRows(r.data.associations || []))
      .finally(() => setLoading(false));
  };

  const loadEvents = () => {
    setEventsLoading(true);
    return api.get("/events")
      .then((r) => setEvents(r.data.events || []))
      .finally(() => setEventsLoading(false));
  };

  useEffect(() => { load(); loadEvents(); }, []);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-tiny mb-2">Associations</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] mb-1">Associations</h1>
          <p className="text-stone-400 text-sm mt-3 max-w-2xl">
            Umbrella orgs (rodeo circuits, breed associations, jackpot series) that own many events.
            Create one here or tag a video's association from Studio → Event Details.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowSmart(true)}
            className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap hover:!text-orange-400 hover:!border-orange-700"
            data-testid="smart-suggest-btn"
            title="Find groups of similar associations and fold them into one umbrella"
          >
            <Sparkles size={14} /> Smart Suggest
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="new-association-btn"
          >
            <Plus size={14} /> New Association
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500" data-testid="associations-empty">
          No associations yet. Click <strong className="text-orange-400">New Association</strong> to create one — or open a video in Studio, click Event Details, and set an Association name.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="associations-grid">
          {rows.map((a) => (
            <Link
              key={a.name}
              to={`/associations/${encodeURIComponent(a.name)}`}
              className="border border-stone-800 hover:border-orange-700 p-5 transition-colors group flex flex-col relative"
              data-testid={`association-card-${a.name.replace(/\s+/g, "_")}`}
            >
              <div className="flex items-start justify-between mb-3">
                <Building2 size={22} className="text-orange-500 shrink-0" />
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500 text-right leading-tight">
                    {(a.admins?.length || 0)} admin{(a.admins?.length || 0) === 1 ? "" : "s"}
                  </span>
                  <DeleteButton
                    testId={`delete-association-${a.name.replace(/\s+/g, "_")}`}
                    label={a.name}
                    onDelete={async () => {
                      const r = await api.delete(`/associations/${encodeURIComponent(a.name)}`);
                      load();
                      return r;
                    }}
                    successMessage={`"${a.name}" removed`}
                  />
                </div>
              </div>
              <div className="font-display uppercase text-xl font-semibold leading-tight group-hover:text-orange-400 truncate">
                {a.name}
              </div>
              {a.description && (
                <div className="text-xs text-stone-500 mt-1 line-clamp-2">{a.description}</div>
              )}
              <div className="text-xs text-stone-400 mt-3 flex flex-wrap gap-x-3 gap-y-1 font-mono-tc">
                <span className="inline-flex items-center gap-1"><Calendar size={11} /> {a.event_count} performances</span>
                <span className="inline-flex items-center gap-1"><Film size={11} /> {a.clip_count} clips</span>
                <span className="inline-flex items-center gap-1"><Users size={11} /> {a.video_count} videos</span>
              </div>
              {(a.events?.length || 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-800/70 space-y-1" data-testid={`association-events-preview-${a.name.replace(/\s+/g, "_")}`}>
                  <div className="label-tiny text-stone-600">Performances under this</div>
                  {a.events.slice(0, 4).map((e) => (
                    <div key={e.video_id || e.name} className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="text-stone-300 truncate">{e.name}</span>
                      <span className="text-stone-500 font-mono-tc shrink-0">
                        {e.clip_count} clip{e.clip_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  ))}
                  {a.event_count > 4 && (
                    <div className="text-[10px] text-stone-600 pt-1">
                      + {a.event_count - 4} more…
                    </div>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAssociationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {showSmart && (
        <SmartSuggestModal
          onClose={() => setShowSmart(false)}
          onMerged={() => { setShowSmart(false); load(); loadEvents(); }}
        />
      )}

      {/* ─────── All performances (flat view across every association) ─────── */}
      <div className="mt-14 pt-8 border-t border-stone-800">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="label-tiny mb-2">All performances</div>
            <h2 className="font-display uppercase text-3xl font-bold leading-[0.95]">Performances</h2>
            <p className="text-stone-500 text-xs mt-2 max-w-xl">
              Every performance (single rodeo/show) across every association — date, discipline count, and clip count in one view.
              Click through to jump straight into the performance's clips.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={eventQ}
              onChange={(e) => setEventQ(e.target.value)}
              placeholder="Search performances…"
              className="field-input text-xs w-56"
              data-testid="events-search"
            />
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc">
              {events.length} total
            </span>
          </div>
        </div>

        {eventsLoading ? (
          <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
            <Loader2 size={13} className="animate-spin" /> Loading events…
          </div>
        ) : events.length === 0 ? (
          <div className="border border-dashed border-stone-700 p-6 text-center text-stone-500 text-sm" data-testid="events-empty">
            No events yet. Upload a video and tag it with an event name.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="events-grid">
            {events
              .filter((e) => {
                const q = eventQ.trim().toLowerCase();
                if (!q) return true;
                return (
                  (e.name || "").toLowerCase().includes(q) ||
                  (e.categories || []).some((c) => (c || "").toLowerCase().includes(q))
                );
              })
              .map((e) => (
                <Link
                  key={e.name}
                  to={`/performances/${encodeURIComponent(e.name)}`}
                  className="border border-stone-800 hover:border-orange-700 p-4 transition-colors group"
                  data-testid={`event-card-${e.name.replace(/\s+/g, "_")}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Calendar size={14} className="text-orange-500 shrink-0 mt-0.5" />
                    <span className="text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc">
                      {e.clip_count} clip{e.clip_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="font-display uppercase text-sm font-semibold leading-tight group-hover:text-orange-400 line-clamp-2">
                    {e.name}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-1.5 flex items-center gap-2 flex-wrap font-mono-tc">
                    {e.date && <span>{e.date}</span>}
                    {(e.categories || []).slice(0, 2).map((c) => (
                      <span key={c} className="inline-block px-1.5 py-0.5 border border-stone-800 text-stone-400 uppercase text-[9px] tracking-widest">
                        {c.replace(/_/g, " ")}
                      </span>
                    ))}
                    {e.needs_event_name && (
                      <span className="text-amber-500 uppercase text-[9px] tracking-widest">
                        needs name
                      </span>
                    )}
                  </div>
                </Link>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateAssociationModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/associations", { name: name.trim(), description: description.trim() });
      toast.success(`Created "${name.trim()}"`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-association-modal">
      <div className="bg-stone-950 border border-orange-700 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl font-bold">New Association</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-tiny block mb-1">Name</label>
            <AutocompleteInput
              value={name}
              onChange={setName}
              field="association"
              placeholder="e.g. Canadian Cowboys Association"
              className="field-input w-full"
              data-testid="new-association-name"
              autoFocus
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">Description (optional)</label>
            <AutocompleteInput
              as="textarea"
              field="description"
              value={description}
              onChange={setDescription}
              rows={3}
              className="field-input w-full resize-none"
              placeholder="A short note admins will see on the association page."
              data-testid="new-association-description"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
            data-testid="new-association-submit"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {busy ? "Creating…" : "Create Association"}
          </button>
        </form>
      </div>
    </div>
  );
}


function SmartSuggestModal({ onClose, onMerged }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    api.get("/associations/merge-suggestions")
      .then((r) => setGroups(r.data.groups || []))
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not load suggestions"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="smart-suggest-modal"
    >
      <div
        className="bg-stone-950 border border-orange-700 max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-800">
          <div>
            <div className="label-tiny">Smart Suggest</div>
            <div className="font-display uppercase text-lg leading-tight">Merge similar associations</div>
            <div className="text-[11px] text-stone-500 mt-1">
              Groups sharing a common name-root — pick one to fold every member into a single umbrella.
            </div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200" data-testid="smart-suggest-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
              <Loader2 size={13} className="animate-spin" /> Scanning associations…
            </div>
          ) : groups.length === 0 ? (
            <div className="border border-dashed border-stone-700 p-6 text-center text-stone-500 text-sm">
              <Check size={20} className="mx-auto mb-2 text-green-500" />
              Nothing to merge — no obvious duplicate groups found.
            </div>
          ) : (
            groups.map((g) => (
              <SmartSuggestRow
                key={g.key}
                group={g}
                expanded={expandedKey === g.key}
                onToggle={() => setExpandedKey(expandedKey === g.key ? null : g.key)}
                onMerged={onMerged}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function SmartSuggestRow({ group, expanded, onToggle, onMerged }) {
  const [umbrella, setUmbrella] = useState(group.suggested_name);
  const [selected, setSelected] = useState(
    () => new Set(group.members.map((m) => m.name)),
  );
  const [busy, setBusy] = useState(false);

  const toggleMember = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelected(next);
  };

  const merge = async () => {
    const members = Array.from(selected);
    if (members.length < 2) {
      toast.error("Pick at least 2 members to merge");
      return;
    }
    if (!umbrella.trim()) {
      toast.error("Umbrella name required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/associations/merge", {
        umbrella_name: umbrella.trim(),
        member_names: members,
      });
      toast.success(
        `Merged into "${r.data.umbrella_name}" · ${r.data.videos_updated} videos, ${r.data.clips_updated} clips retagged`,
      );
      onMerged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-stone-800 hover:border-stone-700 transition-colors" data-testid={`smart-suggest-group-${group.key.replace(/\s+/g, "_")}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3"
      >
        <GitMerge size={16} className="text-orange-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-sm font-semibold truncate">
            {group.suggested_name}
          </div>
          <div className="text-[11px] text-stone-500 font-mono-tc">
            {group.member_count} members · {group.total_videos} videos · {group.total_clips} clips
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-stone-500 shrink-0">
          {expanded ? "Hide" : "Review"}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-stone-800/50 space-y-3">
          <div>
            <div className="label-tiny mb-1.5">Umbrella name</div>
            <div className="flex items-center gap-2">
              <input
                value={umbrella}
                onChange={(e) => setUmbrella(e.target.value)}
                maxLength={120}
                className="field-input flex-1"
                data-testid={`smart-suggest-umbrella-${group.key.replace(/\s+/g, "_")}`}
              />
              {group.suggested_name !== group.alt_suggested_name && (
                <button
                  type="button"
                  onClick={() => setUmbrella(group.alt_suggested_name)}
                  className="text-[10px] px-2 py-1.5 border border-stone-700 text-stone-400 hover:text-orange-400 hover:border-orange-700 uppercase tracking-widest whitespace-nowrap"
                  title={`Suggest: ${group.alt_suggested_name}`}
                >
                  Use "{group.alt_suggested_name}"
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="label-tiny mb-1.5">Members to fold in ({selected.size}/{group.members.length})</div>
            <div className="border border-stone-800 divide-y divide-stone-800 max-h-64 overflow-y-auto">
              {group.members.map((m) => {
                const on = selected.has(m.name);
                return (
                  <label
                    key={m.name}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs ${on ? "bg-stone-900/50" : ""} hover:bg-stone-900`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleMember(m.name)}
                      className="shrink-0"
                      data-testid={`smart-suggest-member-${m.name.replace(/\s+/g, "_")}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{m.name}</div>
                      <div className="text-[10px] text-stone-500 font-mono-tc">
                        {m.video_count} video{m.video_count === 1 ? "" : "s"} · {m.clip_count} clip{m.clip_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onToggle}
              className="btn-secondary text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={merge}
              disabled={busy || selected.size < 2 || !umbrella.trim()}
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`smart-suggest-merge-${group.key.replace(/\s+/g, "_")}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <GitMerge size={12} />}
              {busy ? "Merging…" : `Merge ${selected.size} into "${umbrella.slice(0, 30)}${umbrella.length > 30 ? "…" : ""}"`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
